import { useLocalStorage } from '@cheqroom/hooks';

import { useAuth } from './../../context/Authentication.context';
import { NamespacedOverviewViewConfig, OverviewConfig, OverviewViewConfig, ViewType } from './types';

export type { ViewType };

export type OverviewType =
	| 'items-list'
	| 'check-outs-list'
	| 'contacts-list'
	| 'kits-list'
	| 'reservations-list'
	| 'spotchecks-list'
	| 'tickets-list';

export type UseOverviewConfigArgs<Field> = {
	view?: ViewType;
	type: OverviewType;
	namespace?: string;
	defaultConfig?: OverviewViewConfig<Field>;
};

// Type the storage that is stored in the localstorage (which can be namespaced and thus is a hashmap)
export type StorageType<Field> = OverviewConfig & (OverviewViewConfig<Field> | NamespacedOverviewViewConfig<Field>);

/** What a resource config's `useOverviewConfig` hands back. It may forward only the first three. */
export type ConfigReturnType<Field> = readonly [
	OverviewConfig & OverviewViewConfig<Field>, // Getter
	(config: OverviewConfig & OverviewViewConfig<Field>) => OverviewViewConfig<Field>, // Setter
	() => void, // Clear
	...unknown[],
];

/** The hook's own return: the three above, plus a view switch that leaves the column config alone. */
type OverviewConfigHookReturn<Field> = readonly [
	OverviewConfig & OverviewViewConfig<Field>,
	(config: OverviewConfig & OverviewViewConfig<Field>) => OverviewViewConfig<Field>,
	() => void,
	(view: ViewType) => void,
];

// Overload for 'items-list' which will have a namespace
function useOverviewConfig<Field>(
	args: UseOverviewConfigArgs<Field> & { type: 'items-list'; namespace: string | 'all' }
): OverviewConfigHookReturn<Field>;
// Overload for 'tickets-list' which will have a namespace (per work-order type)
function useOverviewConfig<Field>(
	args: UseOverviewConfigArgs<Field> & { type: 'tickets-list'; namespace: string | 'all' }
): OverviewConfigHookReturn<Field>;
// Overload for all other types which will NOT have a namespace
function useOverviewConfig<Field>(
	args: UseOverviewConfigArgs<Field> & {
		type: Exclude<UseOverviewConfigArgs<Field>['type'], 'items-list' | 'tickets-list'>;
	}
): OverviewConfigHookReturn<Field>;
// Fallback for callers whose `type` is only known to be an OverviewType — the shared overview
// shell, which is generic over the resource. Listed last so a literal `type` still picks the
// overload above and keeps the namespace requirement.
function useOverviewConfig<Field>(
	args: UseOverviewConfigArgs<Field> & { type: OverviewType; namespace?: string }
): OverviewConfigHookReturn<Field>;

// Actual implementation
function useOverviewConfig<Field>({
	view,
	type,
	namespace,
	defaultConfig,
}: UseOverviewConfigArgs<Field>): readonly [
	StorageType<Field>,
	(config: OverviewConfig & OverviewViewConfig<Field>) => void,
	() => void,
	(view: ViewType) => void,
] {
	const defaultOverviewConfig: OverviewConfig = { view: 'table', pageSize: 25 };
	const defaultViewConfig = defaultConfig ?? defaultViewConfigs[type];
	const { id: userId } = useAuth();

	if ((type === 'items-list' || type === 'tickets-list') && !namespace) {
		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.warn(`No namespace provided for ${type} - ${view ?? 'NO VIEW'}`);
		}
	}

	// Prefix of the storage key
	// e.g. 000app.kits-list
	const storageKeyPrefix = `${userId!}app.${type}`;

	// Config for the overview (usually the last used view and pagination size)
	const [overviewConfig, setOverviewConfig, clearOverviewConfig] = useLocalStorage<OverviewConfig>(
		`${storageKeyPrefix}.config`,
		defaultOverviewConfig
	);

	// The specific view config for the overview (e.g. the columns/fields to show)
	// The content of the config CAN be a hashmap where the key is the namespace
	let initialViewConfig: OverviewViewConfig<Field> | NamespacedOverviewViewConfig<Field> | undefined =
		defaultViewConfigs[type];
	if (defaultViewConfig) {
		if (namespace) {
			initialViewConfig = {
				[namespace]: defaultViewConfig,
			};
		} else {
			initialViewConfig = defaultViewConfig;
		}
	} else {
		// This will probably trigger a hook count error, but this is a developer error so we want to know about it
		throw new Error(`No default view config found for type ${type}`);
	}

	const [_overviewViewConfig, setOverviewViewConfig, clearOverviewViewConfig] = useLocalStorage<
		OverviewViewConfig<Field> | NamespacedOverviewViewConfig<Field>
	>(`${storageKeyPrefix}.${view ?? overviewConfig?.view ?? defaultOverviewConfig.view}`, initialViewConfig);

	const overviewViewConfig = namespace
		? (_overviewViewConfig as NamespacedOverviewViewConfig<Field>)
		: (_overviewViewConfig as OverviewViewConfig<Field>);

	// For items-list, set the config for a specific namespace
	const handleSetOverviewConfig = (config: OverviewConfig & OverviewViewConfig<Field>) => {
		const { view, pageSize, ...rest } = config;
		// Set the view config, this is not namespaced
		setOverviewConfig({
			view,
			pageSize,
		});

		if (namespace) {
			// Namespaced setter
			setOverviewViewConfig({
				...overviewViewConfig,
				[namespace]: rest,
			});
		} else {
			// Plain setter
			setOverviewViewConfig(rest as OverviewViewConfig<Field>);
		}
	};

	// The setter above also saves `rest` into the current view's column config. A caller holding
	// only a fallback config would save that fallback there, so switching view goes through this.
	const handleSetView = (nextView: ViewType) => {
		setOverviewConfig({ ...(overviewConfig ?? defaultOverviewConfig), view: nextView });
	};

	// Clear the config from the localstorage
	const handleClearOverviewConfig = () => {
		clearOverviewConfig();
		clearOverviewViewConfig();
	};

	if (namespace) {
		return [
			{
				...(overviewConfig ?? defaultOverviewConfig),
				...(overviewViewConfig
					? ((overviewViewConfig as NamespacedOverviewViewConfig<Field>)[namespace] ?? defaultViewConfig)
					: defaultViewConfig),
			} as StorageType<Field>,
			handleSetOverviewConfig,
			handleClearOverviewConfig,
			handleSetView,
		] as const;
	} else {
		return [
			{
				...(overviewConfig ?? defaultOverviewConfig),
				...overviewViewConfig,
			} as StorageType<Field>,
			handleSetOverviewConfig,
			handleClearOverviewConfig,
			handleSetView,
		] as const;
	}
}

/**
 * Centralized default configs for all overview types
 * Can be overridden by the defaultConfig prop in the useOverviewConfig hook
 */
const defaultViewConfigs: Record<OverviewType, OverviewViewConfig<any>> = {
	'items-list': {
		fields: ['name', 'category', 'location', 'brand', 'model'],
		inlineFields: [],
	},
	'check-outs-list': {
		fields: ['name', 'items', 'from', 'to', 'contact', 'location', 'label', 'duration', 'created'],
		inlineFields: [],
	},
	'contacts-list': {
		fields: ['name', 'email', 'access', 'user.userRole.name', 'contactGroups.name'],
		inlineFields: [],
	},
	'kits-list': {
		fields: ['name', 'description', 'items'],
		inlineFields: [],
	},
	'reservations-list': {
		fields: ['name', 'items', 'from', 'to', 'contact', 'location', 'label', 'duration', 'created'],
		inlineFields: [],
	},
	'tickets-list': {
		fields: [
			'name',
			'assignee',
			'location',
			'label',
			'description',
			'reporter',
			'created',
			'cancelled',
			'closed',
			'started',
			'modified',
			'archived',
		],
		inlineFields: [],
	},
	'spotchecks-list': {
		fields: ['name', 'created', 'closed', 'progress', 'participants', 'assignees'],
		inlineFields: [],
	},
};

export const getDefaultViewConfigWithInlineFields = <Field>(type: OverviewType, inlineFields: Field[]) => {
	return { ...defaultViewConfigs[type], inlineFields } as OverviewViewConfig<Field>;
};

export default useOverviewConfig;
